import type { AccessCode, AccessCodeMetadata } from '@gat-ship/types'

/**
 * Functional core — group-level decisions over a permission group's items.
 *
 * A `PermissionGroupItem` is an `accessCodeMetadata` entry tagged with its
 * `accessCode`. These pure functions answer "which items are visible / granted
 * / hidden, are all enabled / disabled?" for the rendered group; the component
 * supplies the already-built item list and the role's permission codes.
 */
export type PermissionGroupItem = AccessCodeMetadata & {
  accessCode: AccessCode
}

/** Items that are not fully hidden-when-granted by the current permissions. */
export function visibleGroupPermissionItems(
  items: readonly PermissionGroupItem[],
  permissionCodes: readonly string[]
): PermissionGroupItem[] {
  return items.filter(
    (item) =>
      !item.hiddenWhenPermissionsAreGranted?.every((code) =>
        permissionCodes.includes(code)
      )
  )
}

/** Access codes that are granted and whose prerequisites are also granted. */
export function grantedGroupPermissionItems(
  items: readonly PermissionGroupItem[],
  permissionCodes: readonly string[]
): AccessCode[] {
  const result: AccessCode[] = []
  items.forEach((item) => {
    if (permissionCodes.includes(item.accessCode)) {
      const isRequiredRolesGranted =
        item.requiresPermissions?.every((role) =>
          permissionCodes.includes(role)
        ) ?? true
      if (isRequiredRolesGranted) {
        result.push(item.accessCode)
      }
    }
  })
  return result
}

/**
 * Access codes hidden either because every prerequisite is missing (under the
 * hide-required preference) or because all hidden-when-granted codes are held.
 */
export function hiddenGroupPermissionItems(
  items: readonly PermissionGroupItem[],
  permissionCodes: readonly string[],
  hideRequiredPermissions: boolean
): AccessCode[] {
  const result: AccessCode[] = []
  items.forEach((item) => {
    const isHidden =
      (hideRequiredPermissions &&
        item.requiresPermissions?.every(
          (role) => !permissionCodes.includes(role)
        )) ||
      item.hiddenWhenPermissionsAreGranted?.every((code) =>
        permissionCodes.includes(code)
      )
    if (isHidden) {
      result.push(item.accessCode)
    }
  })
  return result
}

/** Whether every item in the group is granted. */
export function allGroupPermissionsGranted(
  items: readonly PermissionGroupItem[],
  permissionCodes: readonly string[]
): boolean {
  return items.every((item) => permissionCodes.includes(item.accessCode))
}

/** Whether none of the items in the group are granted. */
export function allGroupPermissionsRevoked(
  items: readonly PermissionGroupItem[],
  permissionCodes: readonly string[]
): boolean {
  return items.every((item) => !permissionCodes.includes(item.accessCode))
}
